import { Component, OnDestroy, OnInit } from "@angular/core";
import { ModalController, Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import * as _ from "lodash";
import { distinctUntilChanged, Subscription } from "rxjs";
import * as fromApp from "../../store/app.reducer";
import { AnimationOptions } from "ngx-lottie";
import { FilterPaymentMethodPipe } from "src/app/popups/select-payment-method-popup/filter-payment-method.pipe";
import { getLatestNonKioskOrder } from "../kiosk-order";

@Component({
  selector: "app-precash-order-payment-info-modal",
  templateUrl: "./precash-order-payment-info-modal.component.html",
  styleUrls: ["./precash-order-payment-info-modal.component.scss"],
})
export class PrecashOrderPaymentInfoModalComponent
  implements OnInit, OnDestroy
{
  public order: any;
  public barcodeValue: any;
  public printBarcodeAlignment: any = "center";
  public cashierAnimationOptions: AnimationOptions = {
    path: "https://data.deliverymanager.gr/animations/mobileorder/8.3.1/cashier-person-animation.json",
  };
  public hasAvailablePaymentGateway = true;
  private currentUser: any;
  private cartPaymentGateWays: any;
  private storeNetworkConnected: any;

  private ordersSub: Subscription;
  private unsubscribeBackEvent: Subscription;
  private paymentGatewaysSub: Subscription;
  private authSub: Subscription;

  constructor(
    private modalController: ModalController,
    private store: Store<fromApp.AppState>,
    private platform: Platform
  ) {}

  ngOnInit() {
    this.unsubscribeBackEvent = this.platform.backButton.subscribeWithPriority(
      200,
      () => {
        if (this.platform.is("android")) {
          this.closeModal();
        }
      }
    );

    this.ordersSub = this.store
      .select("orders")
      .pipe(distinctUntilChanged())
      .subscribe((state) => {
        if (state && state.ordersArray) {
          const latestOrder = getLatestNonKioskOrder(state.ordersArray);

          if (
            latestOrder &&
            latestOrder.status === "pendingPayment" &&
            (latestOrder.selectedPaymentMethodID === "pre_cash" ||
              latestOrder.selectedPaymentMethodID === "pre_mobilePOS")
          ) {
            if (!_.isEqual(latestOrder, this.order)) {
              this.order = _.cloneDeep(latestOrder);

              this.setBarcodeValue();
              this.updateAvailablePaymentGateways();
            }
          } else {
            this.modalController.dismiss();
            this.barcodeValue = null;
          }
        }
      });

    this.authSub = this.store
      .select("auth")
      .pipe(distinctUntilChanged())
      .subscribe((state) => {
        let paymentAudienceChanged = false;
        if (state && !_.isEqual(this.currentUser, state.user)) {
          this.currentUser = _.cloneDeep(state.user);
          paymentAudienceChanged = true;
        }
        if (
          state &&
          !_.isEqual(
            this.storeNetworkConnected,
            state.storeNetworkConnected
          )
        ) {
          this.storeNetworkConnected = _.cloneDeep(
            state.storeNetworkConnected
          );
          paymentAudienceChanged = true;
        }
        if (paymentAudienceChanged) {
          this.updateAvailablePaymentGateways();
        }
      });

    this.paymentGatewaysSub = this.store
      .select("paymentGateWays")
      .pipe(distinctUntilChanged())
      .subscribe((state) => {
        if (
          state &&
          state.cartPaymentGateWays &&
          !_.isEqual(this.cartPaymentGateWays, state.cartPaymentGateWays)
        ) {
          this.cartPaymentGateWays = _.cloneDeep(state.cartPaymentGateWays);
          this.updateAvailablePaymentGateways();
        }
      });
  }

  setBarcodeValue() {
    if (
      this.order &&
      this.order.selectedPaymentMethodID &&
      (this.order.selectedPaymentMethodID === "pre_cash" ||
        this.order.selectedPaymentMethodID === "pre_mobilePOS")
    ) {
      this.barcodeValue =
        "88" +
        _.cloneDeep(this.order.store_id.toString()) +
        _.cloneDeep(this.order.order_id.toString());
    } else {
      this.barcodeValue = null;
    }

    console.log("Latest order in precash modal:", this.barcodeValue);
  }

  closeModal() {
    this.modalController.dismiss();
  }

  async openChangePaymentMethod() {
    if (!this.order) {
      return;
    }

    await this.modalController.dismiss({
      action: "changePaymentMethod",
      order: _.cloneDeep(this.order),
    });
  }

  ngOnDestroy() {
    if (this.unsubscribeBackEvent) {
      this.unsubscribeBackEvent.unsubscribe();
    }
    if (this.ordersSub) {
      this.ordersSub.unsubscribe();
    }
    if (this.paymentGatewaysSub) {
      this.paymentGatewaysSub.unsubscribe();
    }
    if (this.authSub) {
      this.authSub.unsubscribe();
    }
  }

  private calculateDeliveryMethod(orderPickup: boolean, dineIn: boolean): string {
    if (dineIn) return "dinein";
    if (orderPickup) return "takeaway";
    return "delivery";
  }

  private updateAvailablePaymentGateways() {
    if (!this.cartPaymentGateWays || !this.order) {
      return;
    }

    const deliveryMethod = this.calculateDeliveryMethod(
      this.order.pickup,
      this.order.dinein
    );

    const filteredCartPaymentGateWays = _.pickBy(
      _.cloneDeep(this.cartPaymentGateWays),
      (method) => {
        return (
          (method.only_delivery && deliveryMethod === "delivery") ||
          (method.only_takeaway && deliveryMethod === "takeaway") ||
          (method.only_dinein && deliveryMethod === "dinein") ||
          (!method.only_delivery &&
            !method.only_takeaway &&
            !method.only_dinein)
        );
      }
    );

    if (
      this.order?.selectedPaymentMethodID === "pre_cash" &&
      filteredCartPaymentGateWays?.pre_cash
    ) {
      delete filteredCartPaymentGateWays.pre_cash;
    }

    if (
      this.order?.selectedPaymentMethodID === "pre_mobilePOS" &&
      filteredCartPaymentGateWays?.pre_mobilePOS
    ) {
      delete filteredCartPaymentGateWays.pre_mobilePOS;
    }

    const paymentMethodPipe = new FilterPaymentMethodPipe();
    const availableGateways = paymentMethodPipe.transform(
      _.cloneDeep(filteredCartPaymentGateWays),
      this.currentUser,
      this.order.pickup,
      this.order.dinein,
      this.storeNetworkConnected
    );

    this.hasAvailablePaymentGateway =
      !!availableGateways && Object.keys(availableGateways).length > 0;
  }
}
